import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, FileText, CheckCircle, XCircle } from 'lucide-react';
import { getPlanograms, Planogram } from '../lib/api';
interface BulkCSVUploadProps {
  onComplete: () => void;
}

interface UploadResult {
  success: boolean;
  created: number;
  errors: number;
  details: {
    created: any[];
    errors: Array<{ row: number; name: string; error: string }>;
  };
}

export function BulkCSVUpload({ onComplete }: BulkCSVUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [planograms, setPlanograms] = useState<Planogram[]>([]);
  const [loadingPlanograms, setLoadingPlanograms] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  useEffect(() => {
    async function loadPlanograms() {
      try {
        setLoadingPlanograms(true);
        const data = await getPlanograms();
        setPlanograms(data);
      } catch (error) {
        console.error('Failed to load planograms:', error);
      } finally {
        setLoadingPlanograms(false);
      }
    }
    loadPlanograms();
  }, []);

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('csv', file);
      
      // Add image file if provided
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await fetch('/api/admin/machines/bulk-upload', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to upload CSV');
      }

      const data = await response.json();
      setResult(data);
      
      if (data.created > 0) {
        onComplete();
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'Name', 'Description', 'Label', 'Pin Color', 'Cost', 'Status', 
      'Availability Type', 'Available SKUs', 'Venue Type', 'Venue Name', 
      'Tier', 'Planogram', 
      'Requires Tender Process', 'Tender Type',
      'Machine Type', 'Resolution', 'Location', 'Capacity', 'Format', 
      'Screen Size', 'Playback Format', 'Duration / Loop', 'Orientation', 
      'Aspect Ratio', 'Address Line 1', 'Address Line 2', 'City', 'County', 
      'Post Code', 'Latitude', 'Longitude', 'Image URL'
    ];
    
    const csvContent = headers.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'machines_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Bulk CSV Upload</h3>
        <p className="text-sm text-slate-600 mb-4">
          Upload a CSV file to add multiple machines at once. Download the template below to see the required format.
        </p>
        
        <Button
          type="button"
          variant="outline"
          onClick={downloadTemplate}
          className="mb-4"
        >
          <FileText className="mr-2 h-4 w-4" />
          Download CSV Template
        </Button>
      </div>

      <div className="border rounded-lg p-4 bg-slate-50">
        <Label className="text-sm font-semibold mb-2 block">Available Planograms</Label>
        <p className="text-xs text-slate-600 mb-3">
          Use these exact names in the "Planogram" column of your CSV:
        </p>
        {loadingPlanograms ? (
          <p className="text-sm text-slate-500 italic">Loading planograms...</p>
        ) : planograms.length === 0 ? (
          <p className="text-sm text-slate-500 italic">
            No planograms found. Create one in Machine Editor first, or leave the Planogram column empty.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {planograms.map((planogram) => (
              <span
                key={planogram.id}
                className="inline-block px-3 py-1.5 bg-white border border-slate-200 rounded-md text-sm font-mono text-slate-700 hover:bg-slate-50 transition-colors cursor-default"
                title={planogram.description || planogram.name}
              >
                {planogram.name}
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="csv-file">Select CSV File *</Label>
        <Input
          id="csv-file"
          type="file"
          accept=".csv,text/csv,text/plain,application/csv"
          onChange={handleFileChange}
          disabled={uploading}
        />
        {file && (
          <p className="text-sm text-slate-500 mt-2">
            Selected: {file.name}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="image-file">Image for All Machines (optional)</Label>
        <p className="text-xs text-slate-500 mb-2">
          Upload one image to apply to all machines in the CSV
        </p>
        <Input
          id="image-file"
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          disabled={uploading}
        />
        {imageFile && (
          <p className="text-sm text-slate-500 mt-2">
            Image selected: {imageFile.name}
          </p>
        )}
      </div>

      <Button
        onClick={handleUpload}
        disabled={!file || uploading}
        className="w-full"
      >
        {uploading ? (
          <>
            <Upload className="mr-2 h-4 w-4 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Upload className="mr-2 h-4 w-4" />
            Upload CSV
          </>
        )}
      </Button>

      {result && (
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            {result.errors === 0 ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-orange-600" />
            )}
            <h4 className="font-semibold">Upload Complete</h4>
          </div>
          
          <div className="space-y-1 text-sm">
            <p className="text-green-600">
              <strong>{result.created}</strong> machines created successfully
            </p>
            {result.errors > 0 && (
              <p className="text-orange-600">
                <strong>{result.errors}</strong> errors occurred
              </p>
            )}
          </div>

          {result.details.errors.length > 0 && (
            <div className="mt-3">
              <h5 className="text-sm font-semibold mb-2">Errors:</h5>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {result.details.errors.map((error, index) => (
                  <div key={index} className="text-sm text-red-600">
                    Row {error.row}: {error.name || 'Unknown'} - {error.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

